import { useEffect, useRef } from 'react'
import type { DeviceSpecDevice } from '../../types'
import { newKey } from './model'

// 디바이스 목록 조작 (일반 화면·편집 모드 공통) — 모두 새 배열을 돌려준다

export function moveDevice<T extends { key: string }>(devices: T[], key: string, to: number): T[] {
  const from = devices.findIndex(d => d.key === key)
  if (from < 0 || to < 0 || to >= devices.length || from === to) return devices
  const next = [...devices]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

export function duplicateDevice(devices: DeviceSpecDevice[], key: string): { devices: DeviceSpecDevice[]; copy: DeviceSpecDevice | null } {
  const i = devices.findIndex(d => d.key === key)
  if (i < 0) return { devices, copy: null }
  const src = devices[i]
  const copy: DeviceSpecDevice = {
    ...src, key: newKey(), code: '', name: `${src.name || '디바이스'} (복사)`, channel: { ...src.channel },
    tags: src.tags.map(t => ({ ...t, key: newKey() })),
  }
  return { devices: [...devices.slice(0, i + 1), copy, ...devices.slice(i + 1)], copy }
}

// 중복 — 같은 디바이스 안의 주소, 명세서 전체의 Alias (빈 값 제외). 편집 모드 [중복 표시]
export function findDuplicates(devices: DeviceSpecDevice[]): { marks: Record<string, Partial<Record<'address' | 'alias', true>>>; count: number } {
  const marks: Record<string, Partial<Record<'address' | 'alias', true>>> = {}
  let count = 0
  const mark = (key: string, field: 'address' | 'alias') => { (marks[key] ??= {})[field] = true; count++ }
  for (const d of devices) {
    const byAddress = new Map<string, string[]>()
    for (const t of d.tags) if (t.address.trim()) byAddress.set(t.address.trim(), [...(byAddress.get(t.address.trim()) ?? []), t.key])
    for (const keys of byAddress.values()) if (keys.length > 1) keys.forEach(k => mark(k, 'address'))
  }
  const byMes = new Map<string, string[]>()
  for (const d of devices) for (const t of d.tags) if (t.alias.trim()) byMes.set(t.alias.trim(), [...(byMes.get(t.alias.trim()) ?? []), t.key])
  for (const keys of byMes.values()) if (keys.length > 1) keys.forEach(k => mark(k, 'alias'))
  return { marks, count }
}

// Ctrl+Z / Ctrl+Y(Ctrl+Shift+Z) — 글자 입력 중(input·textarea)에는 그 칸 자체의 되돌리기에 맡긴다
export function useUndoKeys(undo: () => void, redo: () => void, save: () => void) {
  const ref = useRef({ undo, redo, save })
  useEffect(() => { ref.current = { undo, redo, save } })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return
      const key = e.key.toLowerCase()
      if (key === 's') { e.preventDefault(); ref.current.save(); return }
      const tag = (document.activeElement as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); ref.current.undo() }
      else if (key === 'y' || (key === 'z' && e.shiftKey)) { e.preventDefault(); ref.current.redo() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}
